import { useState } from 'react'
import { Link } from 'react-router-dom'
import { api, qs } from '../../lib/api.js'
import {
  Card, Empty, ErrorBox, Field, Modal, Page, Spinner, SubmitButton, useAsync, useConfirm, useToast
} from '../../components/Ui.jsx'
import { lakh } from '../../lib/format.js'

const BLANK = {
  name: '', phone: '', village: '', mandal: '', district: '',
  lat: '', lng: '', pondSalinityPpt: '', pondAreaAcres: '', notes: ''
}

// The inland culture belts the desk delivers into most often.
const PRESETS = [
  { label: 'Bhimavaram', lat: 16.5449, lng: 81.5212, district: 'West Godavari' },
  { label: 'Eluru', lat: 16.7107, lng: 81.0952, district: 'Eluru' },
  { label: 'Bapatla', lat: 15.9044, lng: 80.4675, district: 'Bapatla' },
  { label: 'Chirala', lat: 15.8236, lng: 80.3520, district: 'Bapatla' },
  { label: 'Nellore', lat: 14.4426, lng: 79.9865, district: 'Nellore' },
  { label: 'Amalapuram', lat: 16.5786, lng: 82.0060, district: 'Konaseema' }
]

export default function Farmers () {
  const [q, setQ] = useState('')
  const [search, setSearch] = useState('')
  const { data, error, loading, reload } = useAsync(() => api.get(`/operator/farmers${qs({ q: search })}`), [search], { live: ['farmers'] })
  const toast = useToast()
  const confirm = useConfirm()
  const [adding, setAdding] = useState(false)
  const [form, setForm] = useState(BLANK)
  const [busy, setBusy] = useState(false)

  const submit = async e => {
    e.preventDefault()
    const ok = await confirm({
      title: 'Add this farmer to the book?',
      body: 'Check the phone number and the pond coordinates — matching ranks hatcheries by road distance from that point.',
      facts: [
        ['Name', form.name],
        ['Phone', form.phone],
        ['Pond', [form.village, form.mandal, form.district].filter(Boolean).join(', ') || 'not stated'],
        ['Coordinates', `${form.lat || '?'}, ${form.lng || '?'}`]
      ],
      confirmLabel: 'Add farmer'
    })
    if (!ok) return
    setBusy(true)
    try {
      const { farmer } = await api.post('/operator/farmers', form)
      toast.ok(`${farmer.name} added. You can book for him now.`)
      setForm(BLANK)
      setAdding(false)
      reload()
    } catch (err) { toast.error(err.message) } finally { setBusy(false) }
  }

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))
  const farmers = data?.farmers || []

  return (
    <Page
      title="Farmers"
      subtitle="Farmers never sign in — the desk books for them over the phone. Pond coordinates are what make the distance ranking work."
      actions={<button className="btn primary" onClick={() => setAdding(true)}>Add farmer</button>}
    >
      <Card padded={false}>
        <div className="filterbar">
          <form onSubmit={e => { e.preventDefault(); setSearch(q) }} style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
            <label className="f">
              <span>Search</span>
              <input value={q} onChange={e => setQ(e.target.value)} placeholder="Name, phone or village" />
            </label>
            <button className="btn">Search</button>
            {search && <button type="button" className="btn ghost" onClick={() => { setQ(''); setSearch('') }}>Clear</button>}
          </form>
          <div style={{ flex: 1 }} />
          <div className="muted small">{farmers.length} farmer(s)</div>
        </div>

        {loading
          ? <Spinner />
          : error
            ? <div className="body"><ErrorBox error={error} onRetry={reload} /></div>
            : (
              <div className="scroll-x">
                <table>
                  <thead>
                    <tr><th>Farmer</th><th>Phone</th><th>Pond</th><th className="num">Salinity</th><th className="num">Area</th><th /></tr>
                  </thead>
                  <tbody>
                    {farmers.map(f => (
                      <tr key={f._id}>
                        <td><b>{f.name}</b>{f.notes && <span className="t-sub">{f.notes}</span>}</td>
                        <td className="mono small">{f.phone}</td>
                        <td>{f.place}<span className="t-sub mono">{f.lat}, {f.lng}</span></td>
                        <td className="num">{f.pondSalinityPpt ?? '—'} ppt</td>
                        <td className="num">{f.pondAreaAcres ?? '—'} ac</td>
                        <td className="right">
                          <Link className="btn sm primary" to={`/operator/match?farmerId=${f._id}`}>Book seed</Link>
                        </td>
                      </tr>
                    ))}
                    {!farmers.length && <tr><td colSpan={6}><Empty>No farmers match that search.</Empty></td></tr>}
                  </tbody>
                </table>
              </div>
              )}
      </Card>

      {adding && (
        <Modal title="Add a farmer" onClose={() => setAdding(false)} wide>
          <form onSubmit={submit}>
            <div className="formgrid">
              <Field label="Name" required><input value={form.name} onChange={e => set('name', e.target.value)} required /></Field>
              <Field label="Phone" required><input value={form.phone} onChange={e => set('phone', e.target.value)} required placeholder="+91 …" /></Field>
              <Field label="Village"><input value={form.village} onChange={e => set('village', e.target.value)} /></Field>
              <Field label="Mandal"><input value={form.mandal} onChange={e => set('mandal', e.target.value)} /></Field>
              <Field label="District"><input value={form.district} onChange={e => set('district', e.target.value)} /></Field>
              <Field label="Pond salinity (ppt)"><input type="number" step="0.5" value={form.pondSalinityPpt} onChange={e => set('pondSalinityPpt', e.target.value)} /></Field>
              <Field label="Pond area (acres)"><input type="number" step="0.5" value={form.pondAreaAcres} onChange={e => set('pondAreaAcres', e.target.value)} /></Field>
              <Field label="Latitude" required><input type="number" step="0.0001" value={form.lat} onChange={e => set('lat', e.target.value)} required /></Field>
              <Field label="Longitude" required><input type="number" step="0.0001" value={form.lng} onChange={e => set('lng', e.target.value)} required /></Field>
              <Field label="Notes" wide><input value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Anything the desk should remember" /></Field>
            </div>
            <div className="chips" style={{ marginBottom: 14 }}>
              <span className="muted small" style={{ alignSelf: 'center', marginRight: 4 }}>Quick fill the belt:</span>
              {PRESETS.map(p => (
                <button type="button" key={p.label} className="chip"
                  onClick={() => setForm(f => ({ ...f, lat: p.lat, lng: p.lng, district: p.district, mandal: f.mandal || p.label }))}>
                  {p.label}
                </button>
              ))}
            </div>
            <div className="btnrow">
              <SubmitButton busy={busy} type="submit">Add farmer</SubmitButton>
              <button type="button" className="btn" onClick={() => setAdding(false)}>Cancel</button>
            </div>
          </form>
        </Modal>
      )}
    </Page>
  )
}
